import { t } from "i18next";

import { errors, RoutesApiConst } from "../constants";
import { IProfileDto, SimpleResult } from "../dto";
import { LoginAuthType } from "../enums";
import { Api } from "../helpers/api/api";
import { getRefreshToken } from "../helpers/auth/auth.helpers";
import {
  IAccessRefreshToken,
  IAuthMfaLoginResponse,
  IAuthMfaResendPayload,
  IAuthMfaVerifyPayload,
  IRefreshTtlInfoDto,
} from "../models";

export class AuthApi {
  static async loginParams(): Promise<
    SimpleResult<LoginAuthType[]> | undefined
  > {
    const response = await Api.get<SimpleResult<LoginAuthType[]>>(
      RoutesApiConst.auth.loginParams,
    );

    return response?.data;
  }

  static async login(payload: {
    username?: string;
    phone?: string;
    email?: string;
    password: string;
    recaptchaToken: string;
  }): Promise<[string, string]> {
    const response = await Api.post<IAccessRefreshToken>(
      RoutesApiConst.auth.login,
      payload,
      undefined,
      undefined,
      false,
    );

    if (response?.data?.access_token && response?.data?.refresh_token) {
      return [response?.data?.access_token, response?.data?.refresh_token];
    }

    throw new Error(t(errors.auth.incorrectUsernameOrPassword));
  }

  static async sessionLogin(payload: {
    username?: string;
    phone?: string;
    email?: string;
    password: string;
    recaptchaToken: string;
    recaptchaKey?: string;
  }): Promise<SimpleResult<IProfileDto | IAuthMfaLoginResponse> | undefined> {
    const response = await Api.post<
      SimpleResult<IProfileDto | IAuthMfaLoginResponse>
    >(RoutesApiConst.auth.sessionLogin, payload, undefined, undefined, false);

    return response?.data;
  }

  static async loginMS(accessToken: string): Promise<[string, string]> {
    const response = await Api.post<IAccessRefreshToken>(
      RoutesApiConst.auth.loginMS,
      { accessToken },
      undefined,
      undefined,
      false,
    );

    if (response?.data?.access_token && response?.data?.refresh_token) {
      return [response?.data?.access_token, response?.data?.refresh_token];
    }

    throw new Error(t("errors.couldntLoginViaMSEntra"));
  }

  /**
   * idToken is forwarded so the backend can verify Microsoft's auth_time after a forced login; the
   * backend validates it against Microsoft's keys and never trusts it as sent (RYDE-7833).
   */
  static async sessionLoginMS({
    accessToken,
    idToken,
  }: {
    accessToken: string;
    idToken?: string;
  }): Promise<IProfileDto | undefined> {
    const response = await Api.post<IProfileDto>(
      RoutesApiConst.auth.sessionLoginMS,
      { accessToken, ...(idToken ? { idToken } : {}) },
      undefined,
      undefined,
      false,
    );

    return response?.data;
  }

  static async forgotPassword({
    email,
    redirectUrl,
  }: {
    email: string;
    redirectUrl: string;
  }): Promise<void> {
    const response = await Api.post(
      RoutesApiConst.auth.forgotPassword,
      { email, redirectUrl },
      undefined,
      undefined,
      false,
    );

    if (response) {
      return;
    }

    throw new Error(t("errors.passwordResetLinkFailed"));
  }

  static async resetPassword({
    token,
    password,
    recaptchaToken,
  }: {
    token: string;
    password: string;
    recaptchaToken: string;
  }): Promise<void> {
    const response = await Api.post(
      RoutesApiConst.auth.resetPassword,
      { token, password, recaptchaToken },
      undefined,
      undefined,
      false,
    );

    if (response) {
      return;
    }

    throw new Error(t("errors.passwordResetLinkFailed"));
  }

  static async refresh(): Promise<[string, string]> {
    const refreshToken = getRefreshToken();

    if (!refreshToken) {
      throw new Error(t(errors.auth.errorWhileRefreshTokens));
    }

    const response = await Api.post<IAccessRefreshToken>(
      RoutesApiConst.auth.refreshBothTokens,
      undefined,
      undefined,
      {
        Authorization: `Bearer ${refreshToken}`,
      },
      false,
    );

    if (response?.data?.access_token && response?.data?.refresh_token) {
      return [response?.data?.access_token, response?.data?.refresh_token];
    }

    throw new Error(t("errors.errorWhileRefreshTokens"));
  }

  static async requestOtp({
    phone,
    recaptchaToken,
  }: {
    phone: string;
    recaptchaToken?: string;
  }): Promise<boolean | undefined> {
    const response = await Api.post<null>(RoutesApiConst.auth.requestOtp, {
      phone,
      recaptchaToken,
    });

    if (response) {
      return true;
    }

    throw new Error(t("errors.errorWhileRequestOTP"));
  }

  static async loginOtp({
    phone,
    code,
    recaptchaToken,
  }: {
    phone: string;
    code: string;
    recaptchaToken?: string;
  }): Promise<string | undefined> {
    const response = await Api.post<{ access_token?: string }>(
      RoutesApiConst.auth.loginOtp,
      { phone, code, recaptchaToken },
      undefined,
      undefined,
      false,
    );

    if (response) {
      return response?.data?.access_token;
    }

    throw new Error(t("errors.errorLoginOTPWrongCode"));
  }

  static async sessionLoginOtp(payload: {
    phone: string;
    code: string;
    recaptchaToken?: string;
    recaptchaKey?: string;
  }): Promise<IProfileDto | undefined> {
    const response = await Api.post<IProfileDto>(
      RoutesApiConst.auth.sessionLoginOtp,
      payload,
      undefined,
      undefined,
      false,
    );

    return response?.data;
  }

  static async sessionLogout(): Promise<void> {
    await Api.post(
      RoutesApiConst.auth.sessionLogout,
      undefined,
      undefined,
      undefined,
      true,
    );
  }

  static async sessionRefreshTtlInfo(): Promise<
    IRefreshTtlInfoDto | undefined
  > {
    const response = await Api.get<IRefreshTtlInfoDto>(
      RoutesApiConst.auth.sessionRefreshTtlInfo,
      undefined,
      undefined,
      undefined,
      true,
    );

    const data = response?.data;

    return data;
  }

  static async mfaVerify({
    mfaChallengeToken,
    code,
    recaptchaToken,
    recaptchaKey,
  }: IAuthMfaVerifyPayload): Promise<SimpleResult<IProfileDto> | undefined> {
    const response = await Api.post<SimpleResult<IProfileDto>>(
      RoutesApiConst.auth.mfaVerify,
      { mfaChallengeToken, code, recaptchaToken, recaptchaKey },
      undefined,
      undefined,
      false,
    );

    return response?.data;
  }

  static async mfaResend({
    mfaChallengeToken,
    recaptchaToken,
    recaptchaKey,
  }: IAuthMfaResendPayload): Promise<SimpleResult<void> | undefined> {
    const response = await Api.post<SimpleResult<void>>(
      RoutesApiConst.auth.mfaResend,
      { mfaChallengeToken, recaptchaToken, recaptchaKey },
      undefined,
      undefined,
      false,
    );

    return response?.data;
  }
}
